import { useMemo, useCallback } from 'react';
import { VENDORS_LIST, VENDOR_NEEDED_ITEM_TYPE } from '../constants/roomConstants';

/**
 * High-performance hook for room calculations with memoization
 * @param {Array} rooms - Array of room objects
 * @param {Array} items - Array of item objects
 * @returns {Object} - Memoized calculations and utilities
 */
export const useRoomCalculations = (rooms, items) => {
  // Difficulty values are maintained server-side by the onRoomUpdated cloud
  // function and stored on the unit doc as `calculatedDifficulty`. Read them
  // straight from the document instead of recalculating on the client.
  const roomDifficulties = useMemo(() => {
    if (!rooms?.length) return new Map();

    const difficultyMap = new Map();
    rooms.forEach(room => {
      difficultyMap.set(room.displayName, room.calculatedDifficulty || 0);
    });
    return difficultyMap;
  }, [rooms]);

  // Cost values are maintained server-side and stored as `calculatedCost`.
  const roomCosts = useMemo(() => {
    if (!rooms?.length) return new Map();

    const costMap = new Map();
    rooms.forEach(room => {
      costMap.set(room.displayName, room.calculatedCost || 0);
    });
    return costMap;
  }, [rooms]);

  // Memoized category needs for all rooms
  const roomCategoryNeeds = useMemo(() => {
    if (!rooms?.length || !items?.length) return new Map();

    const categoryMap = new Map();

    // Group items by category first
    const itemsByCategory = items.reduce((acc, item) => {
      if (!acc[item.itemType]) {
        acc[item.itemType] = [];
      }
      acc[item.itemType].push(item);
      return acc;
    }, {});

    // Add vendor needed category (synthetic category)
    if (!itemsByCategory[VENDOR_NEEDED_ITEM_TYPE]) {
      itemsByCategory[VENDOR_NEEDED_ITEM_TYPE] = [];
    }
    VENDORS_LIST.forEach((item) => {
      itemsByCategory[VENDOR_NEEDED_ITEM_TYPE].push({
        ...item,
        itemType: VENDOR_NEEDED_ITEM_TYPE,
      });
    });

    rooms.forEach(room => {
      const roomNeeds = {};
      Object.entries(itemsByCategory).forEach(([category, categoryItems]) => {
        roomNeeds[category] = categoryItems.some(
          item => parseInt(room[item.itemID]) > 0
        );
      });
      categoryMap.set(room.displayName, roomNeeds);
    });

    return categoryMap;
  }, [rooms, items]);

  // Fast lookup functions
  const getDifficulty = useCallback((roomDisplayName) => {
    return roomDifficulties.get(roomDisplayName) || 0;
  }, [roomDifficulties]);

  const getCost = useCallback((roomDisplayName) => {
    return roomCosts.get(roomDisplayName) || 0;
  }, [roomCosts]);

  const getCategoryNeed = useCallback((roomDisplayName, category) => {
    return roomCategoryNeeds.get(roomDisplayName)?.[category] || false;
  }, [roomCategoryNeeds]);

  return {
    getDifficulty,
    getCost,
    getCategoryNeed,
    roomDifficulties,
    roomCosts,
    roomCategoryNeeds
  };
};